import React, { useEffect, useState } from "react";
import {
  Table,
  Input,
  Button,
  Modal,
  Form,
  Select,
  Space,
  Popconfirm,
  Row,
  Col,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchInvestigation,
  createInvestigation,
  updateInvestigation,
  deleteInvestigation,
} from "../../Redux/Slice/Admin/Investigation";
import { toast } from "react-toastify";

const { Option } = Select;

const LabTestMaster = () => {
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTest, setEditingTest] = useState(null);
  const [form] = Form.useForm();

  const { data: labData, loading: labLoading } = useSelector(
    (state) => state.investigation.fetch
  );
  const {
    data: createData,
    error: createError,
    loading: createLoading,
  } = useSelector((state) => state.investigation.create);
  const {
    data: updateData,
    error: updateError,
    loading: updateLoading,
  } = useSelector((state) => state.investigation.update);
  const {
    data: deleteData,
    error: deleteError,
    loading: deleteLoading,
  } = useSelector((state) => state.investigation.delete);

  useEffect(() => {
    dispatch(fetchInvestigation());
  }, [dispatch]);

  // Handle create
  useEffect(() => {
    if (createData?.status) {
      toast.success(createData.message || "Lab test created successfully!");
      dispatch(fetchInvestigation());
      setIsModalOpen(false);
      form.resetFields();
    }
    if (createError)
      toast.error(createError.message || "Failed to create lab test!");
  }, [createData, createError, dispatch, form]);

  // Handle update
  useEffect(() => {
    if (updateData?.status) {
      toast.success(updateData.message || "Lab test updated successfully!");
      dispatch(fetchInvestigation());
      setIsModalOpen(false);
      form.resetFields();
    }
    if (updateError)
      toast.error(updateError.message || "Failed to update lab test!");
  }, [updateData, updateError, dispatch, form]);

  // Handle delete
  useEffect(() => {
    if (deleteData?.status) {
      toast.success(deleteData.message || "Lab test deleted successfully!");
      dispatch(fetchInvestigation());
    }
    if (deleteError)
      toast.error(deleteError.message || "Failed to delete lab test!");
  }, [deleteData, deleteError, dispatch]);

  const openModal = (record = null) => {
    setEditingTest(record);
    if (record) {
      form.setFieldsValue({
        testName: record.testName,
        type: record.type,
        tat: record.tat,
        icdCode: record.icdCode,
        normalRange: record.normalRange,
        sample: record.sample,
        amount: record.amount,
      });
    } else {
      form.resetFields();
    }
    setIsModalOpen(true);
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        testName: values.testName,
        type: values.type,
        tat: values.tat,
        icdCode: values.icdCode,
        normalRange: values.normalRange,
        sample: values.sample,
        amount: values.amount,
      };
      if (editingTest) {
        dispatch(updateInvestigation({ id: editingTest.id, ...payload }));
      } else {
        dispatch(createInvestigation(payload));
      }
    } catch (err) {
      console.error("Validation or API error:", err);
    }
  };

  const handleDelete = (record) => {
    dispatch(deleteInvestigation(record.id));
  };

  const columns = [
    {
      title: "S No",
      dataIndex: "sno",
      render: (_, record, index) => index + 1,
    },
    { title: "Test Name", dataIndex: "testName" },
    { title: "Type", dataIndex: "type" },
    // { title: "TAT", dataIndex: "tat" },
    { title: "ICD Code", dataIndex: "icdCode" },
    // { title: "Normal Range", dataIndex: "normalRange" },
    // { title: "Sample", dataIndex: "sample" },
    { title: "Amount", dataIndex: "amount" },
    {
      title: "Actions",
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => openModal(record)}
          />
          <Popconfirm
            title="Are you sure to delete this test?"
            okText="Yes"
            cancelText="No"
            onConfirm={() => handleDelete(record)}
            disabled={deleteLoading}
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 20 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <h2>Investigation List</h2>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => openModal()}
        >
          Add Lab Test
        </Button>
      </div>

      <Table
        rowKey="id"
        dataSource={labData?.data || []}
        columns={columns}
        pagination={false}
        loading={labLoading}
      />

      <Modal
        title={editingTest ? "Edit Lab Test" : "Add Lab Test"}
        open={isModalOpen}
        onOk={handleOk}
        onCancel={() => setIsModalOpen(false)}
        okText={editingTest ? "Update" : "Create"}
        cancelText="Cancel"
        maskClosable={false}
        width={700}
        confirmLoading={editingTest ? updateLoading : createLoading}
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="testName"
                label="Test Name"
                rules={[
                  { required: true, message: "Enter test name" },
                  {
                    pattern: /^[A-Za-z0-9\s]+$/,
                    message: "Special characters are not allowed",
                  },
                ]}
              >
                <Input placeholder="Enter test name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="type"
                label="Type"
                rules={[{ required: true, message: "Select type" }]}
              >
                <Select placeholder="Select type">
                  <Option value="Laboratory">Laboratory</Option>
                  <Option value="Radiology">Radiology</Option>
                  <Option value="other">Other</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            {/* <Col span={12}>
              <Form.Item
                name="tat"
                label="TAT"
                rules={[{ required: true, message: "Enter TAT" }]}
              >
                <Input placeholder="Enter turnaround time" />
              </Form.Item>
            </Col> */}
            <Col span={12}>
              <Form.Item
                name="icdCode"
                label="ICD Code"
                rules={[
                  { required: true, message: "Enter ICD Code" },
                  {
                    pattern: /^[A-Za-z0-9\s]+$/,
                    message: "Special characters are not allowed",
                  },
                ]}
              >
                <Input placeholder="Enter ICD Code" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="amount"
                label="Amount"
                rules={[{ required: true, message: "Enter amount" }]}
              >
                <Input type="number" placeholder="Enter amount" />
              </Form.Item>
            </Col>
          </Row>

          {/* <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="normalRange"
                label="Normal Range"
                rules={[{ required: true, message: "Enter normal range" }]}
              >
                <Input placeholder="Enter normal range" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="sample"
                label="Sample"
                rules={[{ required: true, message: "Enter sample type" }]}
              >
                <Input placeholder="Enter sample type" />
              </Form.Item>
            </Col>
          </Row> */}

          {/* <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="amount"
                label="Amount"
                rules={[{ required: true, message: "Enter amount" }]}
              >
                <Input type="number" placeholder="Enter amount" />
              </Form.Item>
            </Col>
          </Row> */}
        </Form>
      </Modal>
    </div>
  );
};

export default LabTestMaster;
